import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './CheckoutModal.css';

// ─── Inner form (must be inside <Elements>) ───────────────────────────────────
function CheckoutForm({ priceId, promotionCodeId, promoDiscount, accessToken, planLabel, planPrice, onSuccess, onClose }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setError(null);
    setLoading(true);

    // Step 1: Confirm the SetupIntent — collects and validates the card
    const { error: setupError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/account',
      },
      redirect: 'if_required',
    });

    if (setupError) {
      setError(setupError.message);
      setLoading(false);
      return;
    }

    // Step 2: Send payment method to backend to create the subscription
    try {
      const res  = await fetch('/api/activate-subscription.php', {
        method:  'POST',
        headers: {
          'Authorization': 'Bearer ' + accessToken,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({
          payment_method_id:  setupIntent.payment_method,
          price_id:           priceId,
          promotion_code_id:  promotionCodeId ?? null,
        }),
      });
      const data = await res.json();

      if (data.success) {
        setDone(true);
        setTimeout(() => onSuccess(), 1800);
      } else {
        setError(data.error || 'Failed to activate subscription. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="checkout-success">
        <div className="checkout-success-icon">✓</div>
        <h3>You're now Premium!</h3>
        <p>Your subscription is active. Enjoy full access.</p>
      </div>
    );
  }

  return (
    <form className="checkout-form" onSubmit={handleSubmit}>
      <div className="checkout-plan-summary">
        <span className="checkout-plan-label">{planLabel}</span>
        <span className="checkout-plan-price">{planPrice}</span>
      </div>

      {promoDiscount && (
        <div className="checkout-promo-applied">
          🏷 <strong>{promoDiscount.name}</strong> — {promoDiscount.percentOff ? `${promoDiscount.percentOff}% off` : `$${(promoDiscount.amountOff / 100).toFixed(2)} off`} applied
        </div>
      )}

      <div className="checkout-elements-wrap">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {error && <p className="checkout-error">{error}</p>}

      <button className="checkout-submit" type="submit" disabled={!stripe || loading}>
        {loading ? <span className="checkout-spinner" /> : 'Confirm & Subscribe'}
      </button>

      <p className="checkout-secure">
        <span className="checkout-lock">🔒</span> Secured by Stripe. We never store your card details.
      </p>
    </form>
  );
}

// ─── Outer modal ──────────────────────────────────────────────────────────────
export default function CheckoutModal({ stripePromise, clientSecret, priceId, promotionCodeId, promoDiscount, accessToken, planLabel, planPrice, onSuccess, onClose }) {
  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary:    '#4caf7d',
      colorBackground: '#1a2332',
      colorText:       '#e8eaed',
      colorDanger:     '#e05252',
      fontFamily:      'system-ui, -apple-system, sans-serif',
      borderRadius:    '8px',
    },
  };

  return (
    <div className="checkout-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="checkout-modal">
        <button className="checkout-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="checkout-header">
          <h2>Complete your order</h2>
          <p>LottoPickle Premium</p>
        </div>

        <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
          <CheckoutForm
            priceId={priceId}
            promotionCodeId={promotionCodeId}
            promoDiscount={promoDiscount}
            accessToken={accessToken}
            planLabel={planLabel}
            planPrice={planPrice}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        </Elements>
      </div>
    </div>
  );
}
